// src/components/student/act/StudentACTDiagnosticList.js
import React, { useState, useEffect } from 'react';
import { auth, db } from '../../../firebaseConfig';
import { collection, doc, getDoc, getDocs, getCountFromServer, query, where } from "firebase/firestore";
import { ACT_SECTION_ORDER, resolveACTSectionPlan } from '../../../utils/act/actStructure';

const styles = {
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px', padding: '20px' },
  card: { backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' },
  cardTitle: { fontSize: '16px', fontWeight: 700, color: '#111827', marginBottom: '8px' },
  cardMeta: { fontSize: '13px', color: '#6b7280', marginBottom: '16px' },
  statusBadge: (status) => {
    const colors = {
      locked: { bg: '#f3f4f6', text: '#6b7280', label: 'Locked' },
      unlocked: { bg: '#eff6ff', text: '#2563eb', label: 'Ready to Start' },
      finished: { bg: '#dcfce7', text: '#166534', label: 'Finished' }
    };
    const c = colors[status] || colors.locked;
    return { display: 'inline-block', padding: '4px 10px', borderRadius: '99px', backgroundColor: c.bg, color: c.text, fontSize: '11px', fontWeight: 700, textTransform: 'uppercase' };
  },
  startBtn: { width: '100%', padding: '10px', marginTop: '16px', borderRadius: '8px', border: 'none', backgroundColor: '#2563eb', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: '13px' },
  disabledBtn: { width: '100%', padding: '10px', marginTop: '16px', borderRadius: '8px', border: 'none', backgroundColor: '#f3f4f6', color: '#9ca3af', fontWeight: 500, cursor: 'not-allowed', fontSize: '13px' },
  
  // Modal
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 },
  modal: { backgroundColor: '#fff', borderRadius: '16px', width: '90%', maxWidth: '500px', padding: '24px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' },
  modalTitle: { fontSize: '18px', fontWeight: 700, color: '#111827', marginBottom: '16px' },
  modalText: { fontSize: '14px', color: '#374151', marginBottom: '12px', lineHeight: 1.6 },
  bulletList: { margin: '12px 0', paddingLeft: '20px', fontSize: '14px', color: '#4b5563' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' },
  cancelBtn: { padding: '10px 16px', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#374151', cursor: 'pointer', fontWeight: 600 },
  confirmBtn: { padding: '10px 16px', borderRadius: '8px', border: 'none', backgroundColor: '#2563eb', color: '#fff', cursor: 'pointer', fontWeight: 600 }
};

function getExtendedMinutes(minutes, percent) {
  const p = [0, 25, 50, 75, 100].includes(Number(percent)) ? Number(percent) : 0;
  return Math.round(minutes * (1 + p / 100));
}

export default function StudentACTDiagnosticList({ onStartTest }) {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmTest, setConfirmTest] = useState(null); 
  const [sectionPlan, setSectionPlan] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!auth.currentUser) return;
      try {
        const testsSnap = await getDocs(collection(db, "act_diagnostic_tests"));
        const allTests = testsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        const accessQ = query(
          collection(db, "student_test_access"), 
          where("studentId", "==", auth.currentUser.uid)
        );
        const accessSnap = await getDocs(accessQ);
        
        const accessMap = {};
        accessSnap.forEach(d => {
          const data = d.data();
          accessMap[data.testId] = data;
        });

        const merged = allTests.map(t => {
          const access = accessMap[t.id] || {};
          let status = 'locked';
          if (access.finished) status = 'finished';
          else if (access.unlocked) status = 'unlocked'; // Admin must unlock it first
          
          return { ...t, status, extendedTimePercent: access.extendedTimePercent || 0 };
        });

        setTests(merged);
      } catch (err) {
        console.error("Error loading tests:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Resolve the selected test's real section counts + limits for the modal.
  useEffect(() => {
    if (!confirmTest) { setSectionPlan(null); return; }
    let cancelled = false;

    (async () => {
      try {
        const counts = {};
        await Promise.all(
          ACT_SECTION_ORDER.map(async (section) => {
            const snap = await getCountFromServer(
              query(
                collection(db, "act_diagnostic_tests", confirmTest.id, "questions"),
                where("subject", "==", section)
              )
            );
            counts[section] = snap.data().count;
          })
        );
        let overrides = {};
        const testSnap = await getDoc(doc(db, "act_diagnostic_tests", confirmTest.id));
        if (testSnap.exists() && testSnap.data().timing) {
          overrides = testSnap.data().timing;
        }
        if (!cancelled) setSectionPlan(resolveACTSectionPlan({ counts, overrides }));
      } catch (err) {
        console.error("Error loading ACT section timing:", err);
        if (!cancelled) setSectionPlan(resolveACTSectionPlan());
      }
    })();

    return () => { cancelled = true; };
  }, [confirmTest]);

  if (loading) return <div style={{ padding: 20 }}>Loading tests...</div>;

  return (
    <>
      <div style={styles.grid}>
        {tests.map(test => (
          <div key={test.id} style={styles.card}>
            <div style={{display:'flex', justifyContent:'space-between', marginBottom: 10}}>
              <span style={styles.statusBadge(test.status)}>{test.status === 'unlocked' ? 'Ready' : test.status}</span>
            </div>
            <h3 style={styles.cardTitle}>{test.id}</h3>
            <p style={styles.cardMeta}>ACT Full Diagnostic</p>
            
            {test.status === 'unlocked' ? (
              <button style={styles.startBtn} onClick={() => setConfirmTest(test)}>Start Test</button>
            ) : test.status === 'finished' ? (
              <button style={styles.disabledBtn} disabled>Finished</button>
            ) : (
              <button style={styles.disabledBtn} disabled>Locked</button>
            )}
          </div>
        ))}
        {tests.length === 0 && <p style={{color:'#6b7280'}}>No tests found.</p>}
      </div>

      {confirmTest && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Start {confirmTest.id}?</h3>
            <p style={styles.modalText}>You are about to begin the ACT Diagnostic.</p>
            
            <ul style={styles.bulletList}>
              {sectionPlan ? (
                ACT_SECTION_ORDER.map((section) => (
                  <li key={section}>
                    {section}:{" "}
                    <strong>
                      {getExtendedMinutes(sectionPlan[section].minutes, confirmTest.extendedTimePercent)} mins
                    </strong>{" "}
                    ({sectionPlan[section].count} Qs)
                  </li>
                ))
              ) : (
                <li>Calculating section timing…</li>
              )}
            </ul>

            <p style={{...styles.modalText, color: '#b91c1c', fontSize: '13px'}}>
              ⚠️ Once you begin, please do not exit the browser.
            </p>

            <div style={styles.modalActions}>
              <button style={styles.cancelBtn} onClick={() => setConfirmTest(null)}>Cancel</button>
              <button style={styles.confirmBtn} onClick={() => onStartTest(confirmTest.id)}>Begin Test</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};